import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Head } from 'vite-react-ssg'
import { getCanonicalUrl } from '../utils/canonical'
import { Eye, Zap, ChevronDown, ChevronUp, Users, Palette, Shield } from 'lucide-react'
import { ColorBlindnessSimulatorWidget, SIMULATION_TYPES } from '../components/ColorBlindnessSimulator'

export default function ColorBlindnessSimulatorPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const toggleFaq = (i: number) => setOpenFaq(openFaq === i ? null : i)

  const colorBlindTypes = SIMULATION_TYPES.filter(t => t.id !== 'normal')

  const designTips = [
    {
      title: 'Never rely on color alone',
      description: 'Always pair color with a second indicator — a label, icon, pattern, or shape. A red/green status indicator should also use "Error" / "Success" text or distinct icons.'
    },
    {
      title: 'Test with sufficient contrast',
      description: 'Many contrast failures are especially harmful for color blind users. Run your palette through the contrast checker — a minimum 4.5:1 ratio helps everyone.'
    },
    {
      title: 'Avoid red-green combinations',
      description: 'Red-green confusion affects roughly 8% of men. Use blue-orange or blue-red instead for charts, alerts, and status indicators.'
    },
    {
      title: 'Use patterns and textures in charts',
      description: 'Data visualizations that rely on color alone exclude many users. Add stripe patterns, dots, or cross-hatching so chart areas are distinguishable without color.'
    },
    {
      title: 'Provide text labels on color-coded maps',
      description: 'Heat maps and choropleth maps are notoriously inaccessible. Always include numerical labels or a textual description of the information.'
    },
    {
      title: 'Test your UI with a grayscale check',
      description: 'Convert your design to grayscale. If you can still understand the interface, you\'re on the right track. If important information disappears, fix it.'
    }
  ]

  const faqs = [
    {
      question: 'How accurate is this color blindness simulator?',
      answer: 'This simulator uses the Machado et al. (2009) physiologically-based model, which is the same algorithm used by Chrome DevTools and other professional tools. It is applied in linearized sRGB color space for accuracy. The results are a scientific approximation — actual perception varies between individuals — but it is accurate enough for professional design testing.'
    },
    {
      question: 'What is the most common type of color blindness?',
      answer: 'Deuteranomaly (green-weakness) is the most common, affecting approximately 5% of men and 0.4% of women. It causes difficulty distinguishing green, yellow, and red. Combined with deuteranopia, protanopia, and protanomaly, red-green color blindness affects around 8% of men of Northern European descent.'
    },
    {
      question: 'Is color blindness the same as seeing in black and white?',
      answer: 'No. The vast majority of color blind people are dichromats or anomalous trichromats — they can still see many colors, but cannot distinguish certain combinations. True achromatopsia (seeing only in greyscale) is extremely rare, affecting roughly 1 in 33,000 people.'
    },
    {
      question: 'Does this tool work without uploading images to a server?',
      answer: 'Yes. All image processing happens entirely in your browser using the Canvas API. Your images are never uploaded anywhere. The simulation runs locally and privately on your device.'
    },
    {
      question: 'What file types are supported?',
      answer: 'You can upload any image format your browser supports: PNG, JPG/JPEG, WebP, GIF (first frame), SVG (rasterized), and BMP. For best results, use PNG or JPG.'
    },
    {
      question: 'Should I design for the worst case (achromatopsia)?',
      answer: 'You should primarily design for deuteranomaly and protanopia/deuteranopia, as these affect the largest group. Achromatopsia is extremely rare. That said, the practices that help all color blind users (pairing color with labels, sufficient contrast) also benefit achromatopsia users.'
    },
    {
      question: 'How do I fix color accessibility issues I find with this tool?',
      answer: 'Start by adding non-color indicators (icons, text labels, patterns). Then adjust your color choices — replace red-green pairs with blue-orange or use different lightness levels to create distinct appearances. Run your color pairs through our Contrast Checker to verify sufficient contrast ratios.'
    }
  ]

  return (
    <>
      <Head>
        <title>Color Blindness Simulator — Free Online Tool | achecker.ca</title>
        <meta
          name="description"
          content="Upload any image and instantly see how it appears with protanopia, deuteranopia, tritanopia, and other types of color blindness. Free, private, browser-based."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={getCanonicalUrl('/color-blindness-simulator')} />
        <meta property="og:title" content="Color Blindness Simulator — Free Online Tool" />
        <meta property="og:description" content="See your designs through the eyes of color blind users. Supports 7 simulation types. Works entirely in your browser." />
        <meta property="og:type" content="website" />
      </Head>

      <div className="space-y-12">
        {/* Hero */}
        <section className="text-center py-12 bg-gradient-to-br from-purple-50 to-violet-100 rounded-2xl">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-6">
            <Eye className="w-8 h-8 text-purple-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Color Blindness Simulator
          </h1>
          <p className="text-xl text-gray-600 mb-4 max-w-3xl mx-auto px-4">
            See your designs, screenshots, and graphics through the eyes of users with color vision deficiency.
            Upload any image to generate instant, scientifically-accurate simulations — entirely in your browser.
          </p>
          <p className="text-base text-gray-500 mb-8 max-w-2xl mx-auto px-4">
            Approximately <strong>300 million people worldwide</strong> have some form of color blindness.
            Roughly <strong>1 in 12 men</strong> are affected. This tool helps you design for all of them.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#simulator-section"
              className="inline-flex items-center justify-center px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
              onClick={(e) => {
                e.preventDefault()
                document.getElementById('simulator-section')?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              <Zap className="w-5 h-5 mr-2" />
              Open Simulator
            </a>
            <Link
              to="/contrast-checker"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-gray-700 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <Palette className="w-5 h-5 mr-2" />
              Contrast Checker
            </Link>
          </div>
        </section>

        {/* Widget */}
        <ColorBlindnessSimulatorWidget />

        {/* Types of Color Blindness */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            The 7 Types of Color Vision Deficiency
          </h2>
          <p className="text-gray-700 mb-6">
            Color blindness is not a single condition — it encompasses a spectrum of deficiencies affecting different cone cells in the eye. Understanding each type helps you design more inclusively.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {colorBlindTypes.map(type => (
              <div key={type.id} className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{type.label}</h3>
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full whitespace-nowrap ml-2">
                    {type.prevalence}
                  </span>
                </div>
                <p className="text-gray-600 text-sm mb-2">{type.description}</p>
                <p className="text-xs text-gray-500">
                  <strong>Affected colors:</strong> {type.affectedColors}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Stats Section */}
        <section className="bg-white rounded-2xl border border-gray-200 p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
            <Users className="w-7 h-7 mr-3 text-purple-600" />
            Who Is Affected?
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { stat: '300M', label: 'People worldwide with color blindness' },
              { stat: '1 in 12', label: 'Men affected by red-green color blindness' },
              { stat: '1 in 200', label: 'Women affected by color blindness' },
              { stat: '99%', label: 'Of color blind people have red-green deficiency' },
            ].map((item, i) => (
              <div key={i} className="text-center p-4 bg-purple-50 rounded-xl">
                <div className="text-3xl font-bold text-purple-700 mb-1">{item.stat}</div>
                <div className="text-sm text-gray-600">{item.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Design Tips */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            6 Design Rules for Color Accessible Interfaces
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {designTips.map((tip, i) => (
              <div key={i} className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{tip.title}</h3>
                    <p className="text-sm text-gray-600">{tip.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">How the Simulation Works</h2>
          <div className="bg-white rounded-lg p-8 border border-gray-200 shadow-sm">
            <p className="text-gray-700 mb-4">
              This simulator uses the <strong>Machado et al. (2009) physiologically-based model</strong> —
              the same algorithm used by Chrome DevTools, Figma, and other professional design tools.
            </p>
            <ol className="space-y-4 text-gray-700">
              {[
                'Your image is drawn onto an off-screen HTML Canvas element entirely within your browser.',
                'Each pixel\'s sRGB values are converted to linearized light values (gamma decoding).',
                'A 3×3 matrix transforms the linear RGB triplet to simulate missing or weakened cone responses.',
                'The result is gamma re-encoded and written back to the canvas for display.',
                'For anomalous trichromacy (protanomaly, deuteranomaly), the matrix is interpolated at 60% severity to simulate weakened (not absent) cone response.',
              ].map((step, i) => (
                <li key={i} className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-bold">{i + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
            <p className="text-sm text-gray-500 mt-6 border-t border-gray-100 pt-4">
              <strong>Privacy:</strong> No image data leaves your device. All processing happens locally in your browser via the Canvas API.
            </p>
          </div>
        </section>

        {/* WCAG Connection */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Color Blindness and WCAG Compliance</h2>
          <div className="bg-white rounded-lg p-8 border border-gray-200 shadow-sm space-y-4">
            <p className="text-gray-700">
              The Web Content Accessibility Guidelines address color vision deficiency in two key criteria:
            </p>
            <div className="space-y-4">
              {[
                {
                  criterion: 'WCAG 1.4.1 — Use of Color (Level A)',
                  description: 'Color must not be the only visual means of conveying information, indicating an action, prompting a response, or distinguishing a visual element. This is the most directly relevant criterion for color blind users.'
                },
                {
                  criterion: 'WCAG 1.4.3 — Contrast (Minimum) (Level AA)',
                  description: 'Text and images of text must have a contrast ratio of at least 4.5:1. Sufficient contrast helps color blind users distinguish content even when hue differences are imperceptible.'
                },
                {
                  criterion: 'WCAG 1.4.11 — Non-text Contrast (Level AA)',
                  description: 'UI components and graphical objects must have a 3:1 contrast ratio. This ensures form fields, buttons, and icons are visible regardless of color perception.'
                }
              ].map((item, i) => (
                <div key={i} className="border-l-4 border-purple-400 pl-4">
                  <h3 className="font-semibold text-gray-900 mb-1">{item.criterion}</h3>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button
                  onClick={() => toggleFaq(i)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-${i}`}
                >
                  <h3 className="text-lg font-medium text-gray-900 pr-4">{faq.question}</h3>
                  {openFaq === i
                    ? <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" aria-hidden="true" />
                    : <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" aria-hidden="true" />
                  }
                </button>
                {openFaq === i && (
                  <div id={`faq-${i}`} className="px-6 pb-4">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 bg-gradient-to-r from-purple-600 to-violet-600 rounded-2xl text-white">
          <h2 className="text-3xl font-bold mb-4">
            Go Further: Run a Full Accessibility Audit
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            Color vision is just one dimension of accessibility. Use AChecker to scan your website for the full range of WCAG 2.2 issues — keyboard navigation, screen reader compatibility, semantic HTML, and more.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              <Zap className="w-5 h-5 mr-2" />
              Run Full Accessibility Audit
            </Link>
            <Link
              to="/contrast-checker"
              className="inline-flex items-center justify-center px-8 py-3 bg-purple-700 text-white rounded-lg font-semibold hover:bg-purple-800 transition-colors border border-purple-500"
            >
              <Shield className="w-5 h-5 mr-2" />
              Check Color Contrast
            </Link>
          </div>
        </section>
      </div>
    </>
  )
}
